import { SampledSeriesData, ScatterSeriesData, CHART_CONFIG } from './types';
import { validateSeriesData, validateSeries } from './chart-validators';
import { formatters } from './chart-formatters';
import { getA11yText } from './chart-text-builders';

/**
 * Decide how many datapoints to keep for alt text,
 * based on total points and data frequency.
 */
export const getMaxSampleCount = (totalPoints: number, isMonthly: boolean): number => {
  if (totalPoints <= 0) return 0;

  const pointsPerYear = isMonthly ? 12 : 52;
  const approxYears = totalPoints / pointsPerYear;

  let max: number;

  if (approxYears <= 1) {
    max = 6;
  } else if (approxYears <= 3) {
    max = 4;
  } else if (approxYears <= 5) {
    max = 5;
  } else if (approxYears <= 10) {
    max = 10;
  } else {
    max = 12;
  }

  return Math.min(totalPoints, max);
};

/**
 * Adjusts the maximum number of samples based on the number of series.
 */
export const adjustMaxForSeriesCount = (max: number, seriesCount: number): number => {
  if (seriesCount === 1) return max;
  if (seriesCount === 2) return Math.max(4, Math.min(max, 6));
  if (seriesCount === 3) return 4;
  return 3;
};
/**
 * Sample datapoints evenly and humanize timestamps.
 * Always includes first and last datapoint.
 *
 * Input:  [timestamp, value][]
 * Output: { timestamp, value, date }[]
 */
export const getSampleDataPoints = (data: [number, any][], max: number, locale: string): { timestamp: number; value: any; date: string }[] => {
  const n = data.length;
  if (n <= max) {
    return data.map(([ts, value]) => ({
      timestamp: ts,
      value,
      date: formatters.humanizeTimestamp(ts, locale),
    }));
  }
  const lastIndex = n - 1;
  const step = lastIndex / (max - 1);

  const sampled: { timestamp: number; value: any; date: string }[] = [];

  for (let i = 0; i < max; i += 1) {
    const idx = Math.round(i * step);
    const [ts, value] = data[idx];

    sampled.push({
      timestamp: ts,
      value,
      date: formatters.humanizeTimestamp(ts, locale),
    });
  }

  const [finalTs, finalValue] = data[lastIndex];
  sampled[sampled.length - 1] = {
    timestamp: finalTs,
    value: finalValue,
    date: formatters.humanizeTimestamp(finalTs, locale),
  };

  return sampled;
};

export const getSampleScatterDataPoints = (data: { x: number; y: number; key?: string }[], max: number): { x: number; y: number; key?: string }[] => {
  if (!Array.isArray(data) || data.length === 0) return [];
  if (max <= 0) return [];
  if (data.length <= max) return data;

  const step = data.length / max;
  const sampled: typeof data = [];

  for (let i = 0; i < max; i++) {
    const index = Math.floor(i * step);
    sampled.push(data[index]);
  }

  return sampled;
};

/**
 * Sample all series for alt text.
 */
export const getSampledSeriesForAlt = (seriesArray: any[], isMonthly: boolean, locale: string): SampledSeriesData[] => {
  if (!validateSeriesData(seriesArray)) {
    return [];
  }

  const validSeries = seriesArray.filter(validateSeries);

  if (!validSeries.length) {
    return [];
  }

  // Filter out series with no data points to avoid shortest being 0
  const seriesWithData = validSeries.filter(s => s.data && s.data.length > 0);

  if (!seriesWithData.length) {
    return [];
  }

  const lengths = seriesWithData.map(s => s.data.length);
  const longest = Math.max(...lengths);
  const shortest = Math.min(...lengths);

  let baseMax = getMaxSampleCount(longest, isMonthly);
  baseMax = adjustMaxForSeriesCount(baseMax, seriesArray.length);
  const maxPerSeries = Math.min(baseMax, shortest);

  if (maxPerSeries === 0) {
    return [];
  }

  const result = seriesWithData.map(series => ({
    name: series.name || getA11yText('Charts.AltText.Common.UntitledSeries', 'en-CA'),
    data: getSampleDataPoints(series.data, maxPerSeries, locale),
  }));

  return result;
};

export const getSampledSeriesForScatter = (scatterData: any[], isMonthly: boolean = false): ScatterSeriesData[] => {
  if (!validateSeriesData(scatterData)) return [];

  const validSeries = scatterData.filter(validateSeries);
  if (!validSeries.length) return [];

  const maxPerSeries = CHART_CONFIG.MAX_SCATTER_POINTS;

  return validSeries.map(series => {
    const seriesName = series.data[0]?.name || series.name || getA11yText('Charts.AltText.Common.UntitledSeries', 'en-CA');

    return {
      name: seriesName,
      data: getSampleScatterDataPoints(series.data || [], maxPerSeries),
    };
  });
};
